import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Check, ShoppingCart, PowerOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 129 — Configuración > Venta Cruzada.
 *
 * Admin puede crear/editar/borrar reglas de venta cruzada.
 * Cada regla exige Equipo + Modelo (obligatorios) y productos del Inventario.
 * La pantalla NO permite guardar sin Modelo.
 */
export default function CrossSellSettings() {
  const [rules, setRules] = useState([]);
  const [deviceCatalog, setDeviceCatalog] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rule or {isNew:true}

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/cross-sell/rules"),
        api.get("/settings"),
        api.get("/parts"),
      ]);
      setRules(r1.data || []);
      setDeviceCatalog(r2.data?.device_catalog || []);
      setParts(r3.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const deleteRule = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"?`)) return;
    try {
      await api.delete(`/cross-sell/rules/${rule.id}`);
      setRules((r) => r.filter((x) => x.id !== rule.id));
      toast.success("Regla eliminada.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="cross-sell-settings">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-black flex items-center gap-2">
          <ShoppingCart size={20} /> Venta Cruzada
        </h1>
        <button
          type="button"
          onClick={() => setEditing({ isNew: true })}
          data-testid="cross-sell-rule-new"
          className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-1.5 text-sm font-semibold"
        >
          <Plus size={14} /> Nueva regla
        </button>
      </div>

      <p className="text-sm text-[#525252] mb-3">
        Cada regla se activa cuando el <b>Equipo</b> y el <b>Modelo</b> de la
        reparación coinciden exactamente. Los productos se seleccionan del
        Inventario existente.
      </p>

      {loading ? (
        <div className="text-center py-8 text-[#525252]">Cargando reglas...</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-[#E5E5E0] text-[#525252]">
          Sin reglas configuradas. Pulsa "Nueva regla" para crear una.
        </div>
      ) : (
        <table className="w-full border border-[#111110] text-sm" data-testid="cross-sell-rules-table">
          <thead className="bg-[#111110] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Regla</th>
              <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Equipo · Modelo</th>
              <th className="px-2 py-1.5 text-center font-mono uppercase tracking-widest text-[10px]">Productos</th>
              <th className="px-2 py-1.5 text-center font-mono uppercase tracking-widest text-[10px]">Estado</th>
              <th className="px-2 py-1.5 text-right font-mono uppercase tracking-widest text-[10px]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E5E0]" data-testid={`cross-sell-rule-${r.id}`}>
                <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{r.device_type_label} · {r.model_label}</td>
                <td className="px-2 py-1.5 text-center">{r.product_ids?.length || 0}</td>
                <td className="px-2 py-1.5 text-center">
                  {r.active ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#6E8B3D] text-white font-mono text-[9px] uppercase tracking-widest"><Check size={9} /> activa</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#525252] text-white font-mono text-[9px] uppercase tracking-widest"><PowerOff size={9} /> inactiva</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    data-testid={`cross-sell-edit-${r.id}`}
                    className="px-2 py-0.5 border border-[#111110] text-xs hover:bg-[#111110] hover:text-white mr-1"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRule(r)}
                    data-testid={`cross-sell-delete-${r.id}`}
                    className="p-1 text-red-700 hover:text-red-900"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <RuleEditor
          initial={editing.isNew ? null : editing}
          deviceCatalog={deviceCatalog}
          parts={parts}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setRules((prev) => {
              const idx = prev.findIndex((x) => x.id === saved.id);
              if (idx >= 0) { const copy = [...prev]; copy[idx] = saved; return copy; }
              return [saved, ...prev];
            });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function RuleEditor({ initial, deviceCatalog, parts, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [deviceTypeId, setDeviceTypeId] = useState(initial?.device_type_id || "");
  const [modelId, setModelId] = useState(initial?.model_id || "");
  const [productIds, setProductIds] = useState(initial?.product_ids || []);
  const [active, setActive] = useState(initial?.active !== false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const models = useMemo(() => {
    const fam = deviceCatalog.find((f) => f.id === deviceTypeId);
    return (fam?.models || []).filter((m) => m.active !== false);
  }, [deviceCatalog, deviceTypeId]);

  const filteredParts = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return parts
      .filter((p) => p.active !== false)
      .filter((p) => !qn ||
        (p.name || "").toLowerCase().includes(qn) ||
        (p.sku || "").toLowerCase().includes(qn))
      .slice(0, 40);
  }, [parts, q]);

  const toggleProduct = (id) => {
    setProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Nombre obligatorio."); return; }
    if (!deviceTypeId) { toast.error("Equipo obligatorio."); return; }
    if (!modelId) { toast.error("Modelo obligatorio."); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), device_type_id: deviceTypeId,
        model_id: modelId, product_ids: productIds, active,
      };
      const r = initial
        ? await api.put(`/cross-sell/rules/${initial.id}`, payload)
        : await api.post("/cross-sell/rules", payload);
      onSaved(r.data);
      toast.success(initial ? "Regla actualizada." : "Regla creada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[65] flex items-center justify-center p-4" data-testid="cross-sell-rule-editor">
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="px-4 py-2 bg-[#111110] text-white flex items-center justify-between">
          <div className="font-display font-bold text-sm">{initial ? "Editar regla" : "Nueva regla"}</div>
          <button onClick={onClose} className="p-1 hover:bg-white hover:text-[#111110]"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Nombre *</div>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="cross-sell-rule-name"
              className="w-full border border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Equipo *</div>
              <select
                value={deviceTypeId}
                onChange={(e) => { setDeviceTypeId(e.target.value); setModelId(""); }}
                data-testid="cross-sell-rule-device"
                className="w-full border border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm bg-white"
              >
                <option value="">— Selecciona —</option>
                {deviceCatalog.filter((f) => f.active !== false).map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Modelo * (depende del Equipo)</div>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                disabled={!deviceTypeId}
                data-testid="cross-sell-rule-model"
                className="w-full border border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm bg-white disabled:opacity-40"
              >
                <option value="">— Selecciona —</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
              Productos ({productIds.length} seleccionados)
            </div>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar en Inventario..." data-testid="cross-sell-rule-search"
              className="w-full border border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm mb-2" />
            <div className="max-h-64 overflow-y-auto border border-[#E5E5E0]">
              {filteredParts.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-sm border-b border-[#F0F0F0] hover:bg-[#F7F7F4] cursor-pointer">
                  <input type="checkbox" checked={productIds.includes(p.id)} onChange={() => toggleProduct(p.id)}
                    data-testid={`cross-sell-rule-product-${p.id}`} />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="font-mono text-[10px] text-[#525252]">{p.sku}</span>
                  <span className="font-semibold text-xs">{Number(p.sale_price).toFixed(2)} €</span>
                </label>
              ))}
              {filteredParts.length === 0 && <div className="p-3 text-xs italic text-[#525252]">Sin resultados.</div>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="cross-sell-rule-active" />
            Regla activa
          </label>
        </div>
        <div className="px-4 py-2 border-t border-[#E5E5E0] bg-[#F7F7F4] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-[#525252] hover:bg-white">Cancelar</button>
          <button onClick={save} disabled={busy || !name.trim() || !deviceTypeId || !modelId}
            data-testid="cross-sell-rule-save"
            className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
            <Save size={14} /> {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
